import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDataAPI } from '../../../utils/fetchData';

const ToursTab = ({ show }) => {
  const navigate = useNavigate();
  const [tourPlans, setTourPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('active');
  const [showTourInfo, setShowTourInfo] = useState(null);
  const [showTourInfoLoading, setShowTourInfoLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setShowTourInfoLoading(true);
    loadTourPlans();
    loadShowTourStatus();
  }, [activeFilter, show]);

  const loadShowTourStatus = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows/tour-status', token);
      
      if (response.data.status === 200 && response.data.data.showTourMap && show?._id) {
        const tourInfo = response.data.data.showTourMap[show._id];
        setShowTourInfo(tourInfo);
      } else {
        setShowTourInfo(null);
      }
    } catch (error) {
      console.error('Error loading show tour status:', error);
      setShowTourInfo(null);
    } finally {
      setShowTourInfoLoading(false);
    }
  };

  const loadTourPlans = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const url = activeFilter === 'all' 
        ? 'user/tm/tour-plans'
        : `user/tm/tour-plans?status=${activeFilter}`;
      
      const response = await getDataAPI(url, token);
      
      if (response.data.status === 200) {
        setTourPlans(response.data.data.tourPlans || []);
      }
    } catch (error) {
      console.error('Error loading tour plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProgressColor = (current, total) => {
    const percentage = (current / total) * 100;
    if (percentage === 100) return 'text-green-500';
    if (percentage >= 50) return 'text-yellow-500';
    return 'text-[#FF9500]';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-[rgba(255,149,0,0.2)] text-[#FF9500]',
      completed: 'bg-green-500/20 text-green-500',
      draft: 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.5)]',
      cancelled: 'bg-red-500/20 text-red-500'
    };
    
    return styles[status] || styles.draft;
  };

  const filters = [
    { id: 'active', label: 'Active' },
    { id: 'completed', label: 'Completed' },
    { id: 'all', label: 'All' }
  ];

  return (
    <div className="w-full px-4 pb-6">
      {/* Show Tour Info if show is part of a tour */}
      {!showTourInfoLoading && (
        <>
          {showTourInfo ? (
            <div className="mb-4 bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-[12px] font-semibold">This Show's Tour</span>
                <button
                  onClick={() => navigate(`/tour-manager/tour-overview/${showTourInfo.tourPlanId}`)}
                  className="text-[#FF9500] text-[11px] hover:opacity-70"
                >
                  View Tour →
                </button>
              </div>
              <p className="text-[rgba(255,255,255,0.7)] text-[11px]">
                {showTourInfo.tourPlanName}
              </p>
            </div>
          ) : (
            <div className="mb-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
              <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                This show is not part of any tour.
              </p>
            </div>
          )}
        </>
      )}

      {/* Filters */}
      <div className="mb-4">
        <div className="flex">
          {filters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                activeFilter === filter.id 
                  ? 'text-white' 
                  : 'text-[rgba(255,255,255,0.5)] hover:text-white'
              }`}
            >
              {filter.label}
              {activeFilter === filter.id && (
                <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF9500]"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tour Plans List */}
      {loading ? (
        <div className="text-center py-[60px]">
          <p className="text-white text-[12px]">Loading tour plans...</p>
        </div>
      ) : tourPlans.length === 0 ? (
        <div className="text-left py-[60px] w-full">
          <h3 className="text-white text-[12px] font-semibold mb-2">
            No {activeFilter !== 'all' ? `${activeFilter} ` : ''}tours found
          </h3>
          <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
            {activeFilter === 'all' 
              ? 'Create your first tour plan to get started.'
              : `No ${activeFilter} tours available right now.`
            }
          </p>
          <button 
            onClick={() => navigate('/tour-manager/view-all')}
            className="bg-[#FF9500] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#FF8800] transition-colors"
          >
            Create Tour Plan
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tourPlans.map(tour => {
            const overallProgress = ((tour.progress.flightsBooked + tour.progress.hotelsBooked) / 
              (tour.progress.flightsTotal + tour.progress.hotelsTotal)) * 100;
            
            return (
              <div
                key={tour._id}
                onClick={() => navigate(`/tour-manager/tour-overview/${tour._id}`)}
                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 cursor-pointer transition-all hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)]"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-white text-[12px] font-semibold mb-1">
                      {tour.name}
                    </h3>
                    <div className="flex items-center gap-2 text-[10px] text-[rgba(255,255,255,0.5)]">
                      <span>🏠 {tour.homeCity}</span>
                      <span>•</span>
                      <span>{tour.totalDays} days</span>
                      <span>•</span>
                      <span>{tour.shows?.length || 0} shows</span>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded whitespace-nowrap ${getStatusBadge(tour.status)}`}>
                    {tour.status.toUpperCase()}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[rgba(255,255,255,0.7)] text-[10px]">Progress</span>
                    <span className={`text-[10px] font-bold ${getProgressColor(
                      tour.progress.flightsBooked + tour.progress.hotelsBooked,
                      tour.progress.flightsTotal + tour.progress.hotelsTotal
                    )}`}>
                      {overallProgress.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#FF9500] to-[#FF7700] transition-all duration-300"
                      style={{ width: `${overallProgress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-[10px]">
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-[rgba(255,255,255,0.7)]">
                      {tour.progress.flightsBooked}/{tour.progress.flightsTotal} flights
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-[rgba(255,255,255,0.7)]">
                      {tour.progress.hotelsBooked}/{tour.progress.hotelsTotal} hotels
                    </span>
                  </div>
                </div>

                {/* Date */}
                <div className="mt-2 pt-2 border-t border-[rgba(255,255,255,0.1)]">
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                    Created {formatDate(tour.createdAt)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ToursTab;

